// src/components/onboarding/AcknowledgePage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  acknowledge,
  getDisclosures,
} from "../../shared/_services/onboarding.services";
import StarsIcon from "../../styles/StarsIcon";
import { Button, Checkbox, Panel, cn } from "../ui";
import Loader from "../Loader";
import OnboardingLayout from "./OnboardingLayout";
import {
  ACK_ACKNOWLEDGED,
  ACK_CONTINUE,
  ACK_H1,
  ACK_HELPER,
  ACK_INTRO,
  ACK_LOAD_FAILED,
  ACK_NOT_READY,
  ACK_SIDE_TITLE,
  ACK_SUPPLEMENTS_HEADING,
  ACK_TICK,
  ACK_TICK_BROCHURE,
  GENERIC_ERROR,
  LOAD_FAILED,
  READ_ONLY_CONTINUE,
  TRY_AGAIN,
} from "./onboardingCopy";
import {
  advisorLine,
  documentsRailFor,
  fullDate,
  railLabelFor,
  versionMeta,
} from "./onboardingFormat";
import { isWrongStage, useStageNavigation } from "./onboardingNav";

const hasFile = (version) =>
  !version.sections && typeof version.fileUrl === "string";
const cannotOpen = (version) => !version.sections && !version.fileUrl;
// A fileRef frame with no fallback: 15s to fire `load` before its row is
// treated as failed too (the tick stays disabled either way).
const FILE_LOAD_TIMEOUT_MS = 15_000;
// Every version the page served, top-level rows and their children: the set
// acknowledge must send back (the server compares it with what it serves).
const servedIds = (rows) =>
  rows.flatMap((r) => [
    r.version.versionId,
    ...(r.children || []).map((c) => c.version.versionId),
  ]);
// A row's own version or any of its supplements the vault could not resolve
// (top-level or a child), or whose frame timed out waiting for `load` — the
// client must see this beside the tick, not only by opening the collapsed
// row (a closed <details> hides it in a real browser, so the notice cannot
// live only inside it).
const failedDocs = (row, timedOut) =>
  [row, ...(row.children || [])].filter(
    (d) => cannotOpen(d.version) || timedOut[d.version.versionId],
  );

function Chevron({ open }) {
  return (
    <StarsIcon
      name="chevron"
      data-slot="onb-chevron"
      className={cn(
        "tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-accent",
        open && "tw-rotate-90",
      )}
    />
  );
}

// `display: block` drops the browser's own disclosure marker in every engine.
const SUMMARY = "tw-block tw-cursor-pointer";
const SUMMARY_LINE = "tw-flex tw-min-h-11 tw-items-center tw-gap-3";

// One document's content, in place: `sections` as text, or a `fileRef`
// version in the browser's inline PDF view (never a bare download link). When
// the vault could not resolve its address there is nothing to show here — the
// row renders that notice itself, beside the tick, since this body is inside
// a collapsed <details> a client may never open.
function DocumentBody({ doc, onLoaded, onTimedOut }) {
  const { version } = doc;
  const timerRef = useRef(null);

  // A per-row timer, cleared on unmount or once `load` fires — whichever
  // comes first. Started on mount, not on open: the frame renders (and can
  // load) inside a collapsed <details> too.
  useEffect(() => {
    if (!hasFile(version)) return undefined;
    timerRef.current = setTimeout(
      () => onTimedOut(version.versionId),
      FILE_LOAD_TIMEOUT_MS,
    );
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one timer per mounted row
  }, [version.versionId]);

  if (version.sections) {
    return (
      <div
        data-slot="onb-doc-body"
        className="tw-mt-3 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-3 tw-text-sm"
      >
        {version.sections.map((s, i) => (
          <React.Fragment key={i}>
            <h4 className="tw-m-0 tw-mb-1 tw-mt-3 tw-text-sm tw-font-bold tw-text-text first:tw-mt-0">
              {s.heading}
            </h4>
            <p className="tw-m-0 tw-mb-3 tw-whitespace-pre-wrap tw-text-text-muted">
              {s.body}
            </p>
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (cannotOpen(version)) {
    return null;
  }
  return (
    <iframe
      data-slot="onb-doc-frame"
      className="tw-mt-3 tw-h-96 tw-w-full tw-rounded-md tw-border tw-border-solid tw-border-divider"
      title={doc.name}
      src={version.fileUrl}
      onLoad={() => {
        clearTimeout(timerRef.current);
        onLoaded(version.versionId);
      }}
    />
  );
}

// /onboarding/acknowledge — "Three documents to keep" (spec § Acknowledge).
export default function AcknowledgePage({
  readOnly = false,
  stage,
  resumed = false,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const go = useStageNavigation();
  const [view, setView] = useState({ status: "loading", rows: [] });
  const [ticks, setTicks] = useState({});
  const [fileLoaded, setFileLoaded] = useState({});
  const [fileTimedOut, setFileTimedOut] = useState({});
  const [sending, setSending] = useState(false);
  // Which rows are open, only to turn their chevrons.
  const [isOpen, setIsOpen] = useState({});
  // expandedMs: how long each row (and each supplement) was open, recorded
  // quietly and never required. openedAt holds the start of an open interval.
  const expanded = useRef({});
  const openedAt = useRef({});

  const load = useCallback(async () => {
    setView((v) => ({ ...v, status: "loading" }));
    try {
      const res = await getDisclosures();
      setView({ status: "ready", rows: res.data.documents || [] });
      setTicks({});
      setFileLoaded({});
      setFileTimedOut({});
      expanded.current = {};
      openedAt.current = {};
    } catch (err) {
      const notReady =
        err?.response?.status === 503 &&
        err.response.data?.code === "documents_not_ready";
      setView({ status: notReady ? "not_ready" : "error", rows: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onToggle = (versionId) => (e) => {
    // React re-dispatches "toggle" to an ancestor <details>'s own handler
    // when a nested <details> (a supplement) toggles, even though the native
    // event does not bubble; e.currentTarget there is the ancestor, still
    // open, and would otherwise overwrite its openedAt and drop the time it
    // was open before the nested toggle.
    if (e.target !== e.currentTarget) return;
    const opened = e.currentTarget.open;
    setIsOpen((o) => ({ ...o, [versionId]: opened }));
    const now = Date.now();
    if (e.currentTarget.open) {
      openedAt.current[versionId] = now;
    } else if (openedAt.current[versionId] != null) {
      expanded.current[versionId] =
        (expanded.current[versionId] || 0) +
        (now - openedAt.current[versionId]);
      delete openedAt.current[versionId];
    }
  };
  const expandedMs = () => {
    const now = Date.now();
    const out = { ...expanded.current };
    for (const [id, start] of Object.entries(openedAt.current)) {
      out[id] = (out[id] || 0) + (now - start);
    }
    return Object.fromEntries(
      Object.entries(out).map(([id, ms]) => [id, Math.round(ms)]),
    );
  };

  const markLoaded = (versionId) => {
    setFileLoaded((m) => ({ ...m, [versionId]: true }));
    setFileTimedOut((m) => ({ ...m, [versionId]: false }));
  };
  const markTimedOut = (versionId) =>
    setFileTimedOut((m) => ({ ...m, [versionId]: true }));
  // A row's tick is enabled once everything in it has loaded: text is loaded
  // on arrival, a file when its inline view fires load.
  const rowLoaded = (row) =>
    [row, ...(row.children || [])].every((d) =>
      d.version.sections
        ? true
        : hasFile(d.version) && fileLoaded[d.version.versionId],
    );

  const rows = view.rows;
  const allTicked = rows.length > 0 && rows.every((r) => ticks[r.kind]);

  const submit = async () => {
    if (readOnly) {
      go(stage);
      return;
    }
    if (!allTicked) {
      enqueueSnackbar(ACK_HELPER, { variant: "info" });
      return;
    }
    setSending(true);
    try {
      const res = await acknowledge({
        versionIds: servedIds(rows),
        expandedMs: expandedMs(),
      });
      go(res.data);
    } catch (err) {
      if (isWrongStage(err)) {
        go(err.response.data);
        return;
      }
      if (err?.response?.data?.code === "revision_changed") {
        await load();
        return;
      }
      enqueueSnackbar(err?.response?.data?.message || GENERIC_ERROR, {
        variant: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const locked = !readOnly && (!allTicked || view.status !== "ready");
  const side = (
    <>
      {!readOnly && view.status === "ready" && !allTicked && (
        <p
          data-slot="onb-muted"
          className="tw-m-0 tw-text-sm tw-text-text-muted"
        >
          {ACK_HELPER}
        </p>
      )}
      <Button
        aria-disabled={locked || undefined}
        disabled={sending || (!readOnly && view.status === "not_ready")}
        onClick={submit}
      >
        {readOnly ? READ_ONLY_CONTINUE : ACK_CONTINUE}
        <StarsIcon name="arrow" className="tw-h-4 tw-w-4 tw-shrink-0" />
      </Button>
    </>
  );

  return (
    <OnboardingLayout
      railLabel={railLabelFor(
        readOnly
          ? documentsRailFor("documents_acknowledge", stage?.stages)
          : stage?.rail,
      )}
      sideTitle={ACK_SIDE_TITLE}
      side={side}
      resumed={resumed}
    >
      <h1
        data-slot="onb-h1"
        className="tw-m-0 tw-text-2xl tw-font-bold tw-text-text max-rail:tw-text-xl"
      >
        {ACK_H1}
      </h1>
      <p data-slot="onb-muted" className="tw-m-0 tw-text-md tw-text-text-muted">
        {ACK_INTRO}
      </p>
      {view.status === "loading" && <Loader />}
      {view.status === "not_ready" && (
        <p
          data-slot="onb-notice"
          className="tw-m-0 tw-rounded-md tw-bg-accent-tint tw-px-4 tw-py-3 tw-text-sm tw-text-text"
          role="alert"
        >
          {ACK_NOT_READY}
        </p>
      )}
      {view.status === "error" && (
        <div role="alert" className="tw-text-sm tw-text-text">
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" size="sm" onClick={load}>
            {TRY_AGAIN}
          </Button>
        </div>
      )}
      {view.status === "ready" &&
        rows.map((row) => {
          const children = row.children || [];
          const tickId = `onb-ack-${row.kind}`;
          const loaded = rowLoaded(row);
          return (
            <Panel
              as="div"
              key={row.version.versionId}
              data-slot="onb-row"
              data-kind={row.kind}
              className="tw-p-4"
            >
              <details onToggle={onToggle(row.version.versionId)}>
                <summary className={SUMMARY}>
                  <div className={SUMMARY_LINE}>
                    <Chevron open={isOpen[row.version.versionId]} />
                    <span
                      data-slot="onb-row-name"
                      className="tw-flex-1 tw-text-md tw-font-bold tw-text-text"
                    >
                      {row.name}
                    </span>
                    <span
                      data-slot="onb-muted"
                      className="tw-text-xs tw-text-text-muted"
                    >
                      {versionMeta(row.version)}
                    </span>
                  </div>
                </summary>
                <DocumentBody
                  doc={row}
                  onLoaded={markLoaded}
                  onTimedOut={markTimedOut}
                />
                {children.length > 0 && (
                  <div
                    data-slot="onb-supplements"
                    className="tw-mt-3 tw-flex tw-flex-col tw-gap-2"
                  >
                    <h3
                      data-slot="onb-h3"
                      className="tw-m-0 tw-text-sm tw-font-bold tw-text-text"
                    >
                      {ACK_SUPPLEMENTS_HEADING}
                    </h3>
                    {children.map((child) => (
                      <details
                        key={child.version.versionId}
                        onToggle={onToggle(child.version.versionId)}
                      >
                        <summary className={SUMMARY}>
                          <div className={SUMMARY_LINE}>
                            <Chevron open={isOpen[child.version.versionId]} />
                            <span
                              data-slot="onb-row-name"
                              className="tw-flex-1 tw-text-sm tw-font-semibold tw-text-text"
                            >
                              {advisorLine(child.advisor)}
                            </span>
                            <span
                              data-slot="onb-muted"
                              className="tw-text-xs tw-text-text-muted"
                            >
                              {versionMeta(child.version)}
                            </span>
                          </div>
                        </summary>
                        <DocumentBody
                          doc={child}
                          onLoaded={markLoaded}
                          onTimedOut={markTimedOut}
                        />
                      </details>
                    ))}
                  </div>
                )}
              </details>
              {failedDocs(row, fileTimedOut).length > 0 && (
                <div
                  data-slot="onb-doc-body"
                  className="tw-mt-3 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-3 tw-text-sm tw-text-text"
                  role="alert"
                >
                  <span>{ACK_LOAD_FAILED}</span>{" "}
                  <Button variant="text" size="sm" onClick={load}>
                    {TRY_AGAIN}
                  </Button>
                </div>
              )}
              <div className="tw-mt-3 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-3">
                <Checkbox
                  id={tickId}
                  label={children.length > 0 ? ACK_TICK_BROCHURE : ACK_TICK}
                  checked={readOnly ? true : Boolean(ticks[row.kind])}
                  disabled={readOnly || !loaded}
                  onChange={(e) =>
                    setTicks((t) => ({ ...t, [row.kind]: e.target.checked }))
                  }
                />
              </div>
              {readOnly && row.acknowledged && (
                <p
                  data-slot="onb-muted"
                  className="tw-m-0 tw-mt-2 tw-text-sm tw-text-text-muted"
                >
                  {ACK_ACKNOWLEDGED(fullDate(row.acknowledged.at))}
                </p>
              )}
            </Panel>
          );
        })}
    </OnboardingLayout>
  );
}
